import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { catchError, filter, of, switchMap, tap } from 'rxjs';
import { EmailService, type EmailPreferences } from '@brafton/feedback-api';

/** The Project Team member whose preferences are open. */
export interface PreferencesMember {
  email: string;
  name: string;
}

/**
 * A Project Team member's email preferences, opened by clicking their card.
 *
 * Two switches that match the opt-out links in every email exactly — emails about this
 * project, and emails about every project — which is how someone who opted out from an
 * email is opted back in. Anyone on the project can change anyone's.
 */
@Component({
  selector: 'app-stakeholder-preferences-dialog',
  templateUrl: './stakeholder-preferences-dialog.html',
  styleUrl: './stakeholder-preferences-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(document:keydown.escape)': 'onClose()' },
})
export class StakeholderPreferencesDialog {
  private readonly emails = inject(EmailService);
  private readonly destroyRef = inject(DestroyRef);

  /** The project (feedback round) the "this project" switch applies to. */
  readonly feedbackRoundId = input('');

  /** The member being edited; the dialog is open while this is set. */
  readonly member = input<PreferencesMember | null>(null);

  readonly close = output<void>();

  protected readonly preferences = signal<EmailPreferences | null>(null);
  protected readonly loading = signal(false);
  protected readonly saving = signal(false);
  protected readonly error = signal<string | null>(null);

  constructor() {
    toObservable(this.member)
      .pipe(
        tap(() => {
          this.preferences.set(null);
          this.error.set(null);
        }),
        filter((member): member is PreferencesMember => member !== null && !!this.feedbackRoundId()),
        tap(() => this.loading.set(true)),
        switchMap((member) =>
          this.emails.getEmailPreferences(this.feedbackRoundId(), member.email).pipe(
            catchError(() => {
              this.error.set('Could not load these email preferences.');
              return of(null);
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((preferences) => {
        this.loading.set(false);
        this.preferences.set(preferences);
      });
  }

  /** Saves straight away when a switch is flipped. */
  protected setPreference(key: 'projectEnabled' | 'allEnabled', enabled: boolean): void {
    const current = this.preferences();
    if (!current || this.saving()) {
      return;
    }

    const next: EmailPreferences = { ...current, [key]: enabled };
    this.preferences.set(next);
    this.saving.set(true);
    this.error.set(null);

    this.emails
      .updateEmailPreferences(this.feedbackRoundId(), next)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (saved) => {
          this.saving.set(false);
          this.preferences.set(saved);
        },
        error: () => {
          this.saving.set(false);
          this.preferences.set(current);
          this.error.set('Could not save that change. Please try again.');
        },
      });
  }

  protected onClose(): void {
    if (this.member()) {
      this.close.emit();
    }
  }
}
